/**
 * Para onde o clique num caso leva (E78.F1) — regra pura, sem I/O.
 *
 * Antes da E78 a lista mandava todo caso não-rascunho para `/casos/:id/workspace`
 * sem olhar a modalidade. Num caso eletrônico não há assinatura contestada: o
 * servidor respondia `CASE_NOT_READY_FOR_WORKSPACE` e a tela caía no wizard,
 * cujo gate exige extração `completed` — e a raia eletrônica marca `skipped`.
 * O caso concluído ficava inalcançável a não ser digitando a URL do laudo.
 *
 * A decisão vive aqui, e não dentro da página, porque é a regra que precisa de
 * teste nos quatro estados; a página só carrega os dados e navega.
 */
import type { CaseRecordData } from '@/lib/cases';
import type { ExameEletronicoCcbData } from '@/lib/exame_eletronico';

/** As telas por onde um caso pode começar. */
export type DestinoDoCaso = 'wizard' | 'pre-analise' | 'mesa-eletronica' | 'workspace' | 'laudo';

const ROTA: Record<DestinoDoCaso, (caseId: string) => string> = {
  wizard: (id) => `/casos/${id}/wizard`,
  'pre-analise': (id) => `/casos/${id}/pre-analise`,
  'mesa-eletronica': (id) => `/casos/${id}/mesa-eletronica`,
  workspace: (id) => `/casos/${id}/workspace`,
  laudo: (id) => `/casos/${id}/laudo`,
};

export function rotaDoDestino(destino: DestinoDoCaso, caseId: string): string {
  return ROTA[destino](caseId);
}

/**
 * Destino de um caso da raia eletrônica, pelo estado das peças.
 *
 * `pecas` é a lista da raia (uma entrada por CCB contestada viva). A ordem das
 * perguntas é a do trabalho: primeiro o que já acabou, depois o que está na
 * mesa, e por último o que ainda nem foi verificado.
 */
export function destinoEletronico(
  caso: CaseRecordData,
  pecas: readonly ExameEletronicoCcbData[],
): DestinoDoCaso {
  if (caso.status === 'draft') return 'wizard';
  // Homologado é terminal: o documento existe e foi aprovado, ainda que uma
  // peça tenha sido reaberta depois.
  if (caso.review_status === 'approved') return 'laudo';
  const concluidas = pecas.filter((p) => p.exame?.status === 'concluido').length;
  if (pecas.length > 0 && concluidas === pecas.length) return 'laudo';
  const algumaVerificada = pecas.some(
    (p) => p.exame?.status === 'verificado' || p.exame?.status === 'concluido',
  );
  return algumaVerificada ? 'mesa-eletronica' : 'pre-analise';
}

/**
 * Destino de um caso grafotécnico — o comportamento de hoje, preservado.
 *
 * A E78.F1 não mexe na esteira visual: rascunho abre a preparação do caso, o
 * resto abre a mesa. O que muda é o caminho até aqui (a lista deixou de decidir
 * a rota) e não a decisão.
 */
export function destinoGrafotecnico(caso: CaseRecordData): DestinoDoCaso {
  return caso.status === 'draft' ? 'wizard' : 'workspace';
}

export function destinoDoCaso(
  caso: CaseRecordData,
  pecas: readonly ExameEletronicoCcbData[],
): DestinoDoCaso {
  return caso.modalidade === 'eletronica'
    ? destinoEletronico(caso, pecas)
    : destinoGrafotecnico(caso);
}
